/**
 * Jev-Messung (Welle 0 des Jev-Optimierungsplans, 28.10.2026) — NUR MESSEN, kein Verhalten.
 *
 * Jede Journalzeile der Jev-Wege bekommt zwei Felder, damit nichts mehr über Zeitnähe zugeordnet werden muss:
 *   az=<Kennung>    kurze Zufalls-ID einer Aufnahme (8 Zeichen a-z0-9, ohne jeden Inhalt). Die App erzeugt sie beim Start der
 *                   Aufnahme und schickt sie mit ([ptt-nachhoeren], /api/stille-auto, Upload); so verbindet sie dieselbe
 *                   Aufnahme über App und Server. `-`: die Zeile gehört zu keiner Aufnahme (Tippen, Start des Dienstes).
 *   regelv=<n>      AUTO_REGEL_VERSION — welche Fassung der Stille-Regel galt.
 * Dazu in `[mikro-nach-antwort]` das Feld `frageAmEnde=0|1` (shared/mikro-nach-antwort.ts).
 *
 * ⚠️ Eine EINZIGE Hilfsfunktion (`mitMessung`) setzt die Felder; die Zeilenbauer bleiben unberührt. Server: am Ausgang
 *    (log/console.log), App: in den zwei Ausgängen meldeDiagnose/meldeSpur (frontend/lib/aufnahme-kennung.ts).
 * ⚠️ Notschalter `JEV_MESSUNG=0` (Vorgabe AN — es sind nur Zahlen): die Server-Zeilen bleiben in der alten Form, und der Server
 *    nimmt die Felder aus den App-Zeilen heraus (`ohneMessung`, server/messzeile-route.ts). Wirkt nach einem Neustart.
 * ⚠️ Die Kennung trägt NIE Inhalt: sie wird geprüft (`aufnahmeKennungAus`), alles andere wird zu `null`.
 */
import { AUTO_REGEL_VERSION } from "./stille-auto.js";

/** So sieht eine gültige Kennung aus (auch die des Nachhörens, server/nachhoeren.ts: 8 bis 64 Zeichen). */
const KENNUNG = /^[a-z0-9]{8}$/;

const ZEICHEN = "abcdefghijklmnopqrstuvwxyz0123456789";

/** Eine neue Kennung: 8 Zeichen aus Zufall, ohne Bezug zu Zeit, Konto oder Inhalt. */
export function neueAufnahmeKennung(): string {
  const bytes = new Uint8Array(8);
  const k = (globalThis as { crypto?: { getRandomValues?: (a: Uint8Array) => Uint8Array } }).crypto;
  if (k?.getRandomValues) k.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  let s = "";
  for (const b of bytes) s += ZEICHEN[b % ZEICHEN.length];
  return s;
}

/** Eine Kennung aus einem rohen Wert (Rumpf, Abfrage) — gültig oder `null`. */
export function aufnahmeKennungAus(roh: unknown): string | null {
  return typeof roh === "string" && KENNUNG.test(roh) ? roh : null;
}

/** Die Messung gilt (Vorgabe an); `JEV_MESSUNG=0` schaltet die neuen Felder ab. */
export function jevMessungAn(env: Record<string, string | undefined> = typeof process !== "undefined" ? process.env : {}): boolean {
  return (env.JEV_MESSUNG ?? "").trim() !== "0";
}

/** Die zwei Felder, mit führendem Leerzeichen. */
export function messFelder(az: string | null | undefined): string {
  return ` az=${aufnahmeKennungAus(az) ?? "-"} regelv=${AUTO_REGEL_VERSION}`;
}

/**
 * Setzt die Felder in eine Zeile: Server-Zeilen (`[tag] uid=N …`) gleich hinter `uid=N`, so bleibt der Rest der Zeile, wie er war;
 * Zeilen ohne `uid=` (App) hinten dran. Eine Zeile mit `az=` bleibt unverändert (nie doppelt). `an=false`: die Zeile bleibt, wie sie ist.
 */
export function mitMessung(zeile: string, az: string | null | undefined, an: boolean = jevMessungAn()): string {
  if (!an || / az=\S/.test(zeile)) return zeile;
  // `[ende-token]` trug `regelv=` schon vorher: nie doppelt.
  const felder = / regelv=/.test(zeile) ? ` az=${aufnahmeKennungAus(az) ?? "-"}` : messFelder(az);
  const m = /^(\[[a-z0-9-]+\] uid=\S+)/.exec(zeile);
  return m?.[1] ? `${m[1]}${felder}${zeile.slice(m[1].length)}` : `${zeile}${felder}`;
}

/** Die Jev-Wege der App — nur deren Zeilen bekommen die Felder (ihre Ausgänge sind für alles andere offen). */
const APP_TAGS = ["ende-token", "ptt-ende", "ptt-nachhoeren", "mikro-nach-antwort", "stille-auto", "abschied", "jev", "jev-zusatz", "jev-paket", "jev-modul", "abschluss-tor", "fortsetzung"];
const APP_TAG = new RegExp(`^\\[(?:${APP_TAGS.join("|")})\\]`);

/** Gehört die Zeile zu den Jev-Wegen? */
export function istJevZeile(zeile: string): boolean {
  return APP_TAG.test(zeile);
}

/**
 * Die Felder aus einer App-Zeile nehmen (Notschalter `JEV_MESSUNG=0`, im Server): zurück zur alten Zeilenform.
 * `regelv=` bleibt in `[ende-token]` (dort gab es das Feld schon vorher).
 */
export function ohneMessung(zeile: string): string {
  if (!istJevZeile(zeile)) return zeile;
  let z = zeile.replace(/ az=\S+/, "").replace(/ frageAmEnde=\S+/, "").replace(/ abl=1/, "");
  if (!z.startsWith("[ende-token]")) z = z.replace(/ regelv=\d+/, "");
  return z;
}
